import { Component, inject, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { LocaleString } from '@grupo-constant/next-dtype/i18n';
import { NextLocator, SelectOption } from '@grupo-constant/next-dtype/types';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { debounceTime, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';
import { SharedModule } from '../../shared/shared.module';
import { adminBrandProfileUri, adminCustomerProfileUri, adminProjectProfileUri, backofficeCustomerProfileUri, backofficeProjectProfileUri, NextBrand, NextCustomer, NextProject, viewerProjectProfileUri } from '../../modules/admin/projects/projects.types';
import { backofficeProfileUri, NextServicio, viewerProfileUri } from '../../modules/admin/services/services.types';
import { developerOUProfileUri, OU_URI, superuserOUProfileUri } from '../../modules/admin/ou/ou.types';
import { DTYPE_BRAND_URI, DTYPE_CUSTOMER_URI, DTYPE_PROJECT_URI, ProjectsService } from '../../modules/admin/projects/projects.service';
import { SERVICE_DTYPE_URI } from '../../modules/admin/services/services.service';
import { CompanyUser } from '../../core/user/companyUser.model';
import { CompanyService } from '../../core/company/company.service';
import { FuseConfirmationService } from '../../../@fuse/services/confirmation/confirmation.service';
import { UserService } from '../../core/user/user.service';

@Component({
    selector: 'app-create-profile-dialog',
    templateUrl: './create-profile-dialog.component.html',
    styles: [
        /* language=SCSS */
        `
            .create-profile-dialog-panel {

                .mat-mdc-dialog-container {

                    .mat-mdc-dialog-surface {
                        padding: 0 !important;
                    }
                }
            }
        `
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SharedModule, TranslocoModule]
})
export class CreateProfileDialogComponent implements OnInit {

    nodeMap: Map<string, (NextBrand | NextCustomer | NextProject | NextServicio)[]>;
    brands: NextBrand[] = [];
    customers: NextCustomer[] = [];
    projects: NextProject[] = [];
    services: NextServicio[] = [];
    profileTypesByNode = new Map<string, string[]>([
        [OU_URI, [superuserOUProfileUri, developerOUProfileUri]],
        [DTYPE_BRAND_URI, [adminBrandProfileUri]],
        [DTYPE_CUSTOMER_URI, [adminCustomerProfileUri, backofficeCustomerProfileUri]],
        [DTYPE_PROJECT_URI, [adminProjectProfileUri, backofficeProjectProfileUri, viewerProjectProfileUri]],
        [SERVICE_DTYPE_URI, [backofficeProfileUri, viewerProfileUri]],
    ]);
    profileTypes: SelectOption[] = [];

    formGroup = new FormGroup({
        clienteCode: new FormControl<number>(undefined),
        marcaCode: new FormControl<string>(undefined),
        projectId: new FormControl<string>(undefined),
        serviceId: new FormControl<string>(undefined),
        user: new FormControl<CompanyUser>(undefined, Validators.required),
        userDatatypeUri: new FormControl<string>(undefined, Validators.required),
    });

    searchInputControl = new FormControl<string | CompanyUser>('');
    searchUsers: Observable<CompanyUser[]>;

    private _unsubscribeAll = new Subject<void>();

    private companyService = inject(CompanyService);
    public data: { marca_code?: string } = inject(MAT_DIALOG_DATA);
    public dialogRef = inject(MatDialogRef<CreateProfileDialogComponent>);
    private _fuseConfirmationService = inject(FuseConfirmationService);
    private projectsService = inject(ProjectsService);
    private transloco = inject(TranslocoService);
    private userService = inject(UserService);

    async ngOnInit() {
        this.nodeMap = await this.projectsService.retrieveVisibilityMap();
        
        this.formGroup.get('marcaCode').valueChanges
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(marcaCode => this.marcaChanged(marcaCode));

        this.formGroup.get('clienteCode').valueChanges
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(clienteCode => this.clienteChanged(clienteCode));

        this.formGroup.get('projectId').valueChanges
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(projectId => this.projectChanged(projectId));

        if (this.data.marca_code) {
            this.formGroup.get('marcaCode').setValue(this.data.marca_code);
            this.formGroup.get('marcaCode').disable();
            this.changeProfileTypes(DTYPE_BRAND_URI);
        } else {
            this.brands = this.nodeMap.get(DTYPE_BRAND_URI) as NextBrand[];
            this.changeProfileTypes(OU_URI);
        }

        this.searchUsers = this.searchInputControl.valueChanges
            .pipe(
                takeUntil(this._unsubscribeAll),
                debounceTime(750),
                switchMap((value) => {
                    if (typeof value === 'object') {
                        this.formGroup.get('user').setValue(value);
                        return of([]);
                    } else {
                        this.formGroup.get('user').setValue(undefined);
                        if (value.length > 0) {
                            return this.userService.searchCompanyUser(value, this.companyService.structureCompanyId);
                        }
                        return of([]);
                    }
                })
            );
    }

    closeDialog() {
        this.dialogRef.close();
    }

    accept() {
        const dialogRef = this._fuseConfirmationService.open({
            title: this.transloco.translate('warning'),
            message: this.transloco.translate('boPermissions.addProfileConfirm'),
            actions: {
                confirm: {
                    label: this.transloco.translate('confirm')
                },
                cancel: {
                    label: this.transloco.translate('cancel')
                }
            }
        });
        dialogRef.afterClosed().subscribe((confirm) => {
            if (confirm === 'confirmed') {
                const locator: NextLocator = {
                    OU: '01',
                    marca_code: this.formGroup.get('marcaCode').value,
                    cliente_code: this.formGroup.get('clienteCode').value,
                    projectId: this.formGroup.get('projectId').value,
                    serviceId: this.formGroup.get('serviceId').value
                };
                this.dialogRef.close({ user: this.formGroup.get('user').value, locator, userDatatypeUri: this.formGroup.get('userDatatypeUri').value });
            }
        })
    }

    displayFn(user: CompanyUser) {
        return user?.displayName || '';
    }

    private marcaChanged(marcaCode: string) {
        if (marcaCode) {
            this.customers = (this.nodeMap.get(DTYPE_CUSTOMER_URI) as NextCustomer[]).filter(customer => customer.marca_code === marcaCode);
            this.changeProfileTypes(DTYPE_BRAND_URI);
        } else {
            this.customers = [];
            this.changeProfileTypes(OU_URI);
        }
        this.formGroup.get('clienteCode').setValue(undefined, { emitEvent: false });
        this.projects = [];
        this.formGroup.get('projectId').setValue(undefined, { emitEvent: false });
        this.services = [];
        this.formGroup.get('serviceId').setValue(undefined, { emitEvent: false });
    }

    private clienteChanged(clienteCode: number) {
        if (clienteCode !== undefined) {
            this.projects = (this.nodeMap.get(DTYPE_PROJECT_URI) as NextProject[]).filter(project => project.cliente_code === clienteCode);
            this.changeProfileTypes(DTYPE_CUSTOMER_URI);
        } else {
            this.projects = [];
            this.changeProfileTypes(DTYPE_BRAND_URI);
        }
        this.formGroup.get('projectId').setValue(undefined, { emitEvent: false });
        this.services = [];
        this.formGroup.get('serviceId').setValue(undefined, { emitEvent: false });
    }

    private projectChanged(projectId: string) {
        if (projectId) {
            this.services = (this.nodeMap.get(SERVICE_DTYPE_URI) as NextServicio[]).filter(service => service.projectId === projectId);
            this.changeProfileTypes(SERVICE_DTYPE_URI);
        } else {
            this.services = [];
            this.changeProfileTypes(DTYPE_PROJECT_URI);
        }
        this.formGroup.get('serviceId').setValue(undefined, { emitEvent: false });
    }

    private changeProfileTypes(uri: string) {
        this.profileTypes = this.profileTypesByNode.get(uri).map(profileUri => {
            const localeString: LocaleString = {};
            for (const language of this.transloco.getAvailableLangs()) {
                const lang = typeof language === 'string' ? language : language.id;
                localeString[lang] = this.transloco.translate('boPermissions.' + this.projectsService.getProfileNameByUri(profileUri), undefined, lang);
            }
            const selectOption: SelectOption = {
                _deprecated: false,
                code: profileUri,
                value: localeString,
                export: {}
            };
            return selectOption;
        });
        this.formGroup.get('userDatatypeUri').setValue(undefined);
    }
}
